import { useEffect, useMemo, useState } from "react";

export const CLIENT_SIDE_PAGE_SIZE_OPTIONS = [10, 15, 20] as const;
export const CLIENT_SIDE_DEFAULT_PAGE_SIZE = CLIENT_SIDE_PAGE_SIZE_OPTIONS[0];

interface UseClientPaginationResult<T> {
  currentPage: number;
  pageSize: number;
  paginatedItems: T[];
  onPaginationChange: (page: number, pageSize: number) => void;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
}

const useClientPagination = <T,>(
  items: T[],
  defaultPageSize: number = CLIENT_SIDE_DEFAULT_PAGE_SIZE
): UseClientPaginationResult<T> => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [items.length, pageSize, currentPage]);

  const onPaginationChange = (page: number, size: number) => {
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
      return;
    }
    setCurrentPage(page);
  };

  const paginatedItems = useMemo(
    () => items.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [items, currentPage, pageSize]
  );

  return {
    currentPage,
    pageSize,
    paginatedItems,
    onPaginationChange,
    setCurrentPage,
    setPageSize,
  };
};

export default useClientPagination;